// Two-factor verification — shown after password sign-in for any account
// with mfa_required (admins by default). Same AuthCard chrome as /login so
// the two screens read as one flow. The code field auto-focuses on load and
// verifies on Enter (or automatically once 6 digits are in), so the normal
// path is: land → type code → done, zero clicks.
import type { Href } from "expo-router";
import { useRouter } from "expo-router";
import { useEffect, useRef, useState } from "react";
import { Linking, Platform, Pressable, Text, View, type TextInput as RNTextInput } from "react-native";

import { AuthCard } from "../../../components/auth/AuthCard";
import { Button, Input } from "../../../components/ui";
import { COLORS, FONTS } from "../../../lib/brand";
import { loadLoginProfile, roleToRoute, signOutToHome } from "../../../lib/auth";
import { challengeAndVerify, firstVerifiedTotpFactorId, listFactors } from "../../../lib/mfa";
import { supabase } from "../../../lib/supabase";

const AMBER = COLORS.AMBER.hex;
const SUBTITLE = "rgba(28,43,26,0.68)";
const SUPPORT_MAIL = "mailto:support@coravu.com?subject=Two-factor%20access";

export default function MfaChallengeScreen() {
  const router = useRouter();
  const [code, setCode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<RNTextInput>(null);

  // Session check runs in the background — the form renders immediately so
  // the sign-in → MFA transition is seamless (same AuthCard chrome, content
  // swaps in place with no loading flash). An unauthenticated visitor gets
  // bounced to /login a frame later; the form is harmless to show meanwhile.
  useEffect(() => {
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session?.user) router.replace("/login" as Href);
    })();
    // router identity is stable in expo-router; run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus the code field so the user can type immediately. The input
  // also gets autoFocus, but RN-web's autoFocus is unreliable, so nudge it
  // via the ref too.
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  async function onVerify(codeOverride?: string) {
    if (busy) return;
    const trimmed = (codeOverride ?? code).replace(/\D/g, "").trim();
    if (trimmed.length !== 6) {
      setErr("Enter the 6-digit code from your app.");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const factors = await listFactors();
      const factorId = firstVerifiedTotpFactorId(factors);
      if (!factorId) {
        setErr("No authenticator is enrolled. You will be redirected to set one up.");
        router.replace("/admin/mfa/enroll" as Href);
        return;
      }
      await challengeAndVerify(factorId, trimmed);
      const profile = await loadLoginProfile();
      if (!profile?.role) {
        setErr("No role found for this account.");
        return;
      }
      router.replace(roleToRoute(profile.role) as Href);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Invalid code, try again");
    } finally {
      setBusy(false);
    }
  }

  function onChangeCode(t: string) {
    const digits = t.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    // Auto-verify the moment a full code is in — Enter and the Verify
    // button both remain as manual paths.
    if (digits.length === 6 && !busy) void onVerify(digits);
  }

  return (
    // HOME must sign out first: a password-authenticated-but-unverified
    // session is otherwise trapped here (the index route sends an authed
    // user to their portal, and PortalGate bounces straight back to this
    // page). Signing out clears the half-authenticated state so the user
    // actually lands on the public site.
    <AuthCard onHome={() => void signOutToHome(router)}>
      <Text
        className="mb-2 text-[10px] font-bold uppercase tracking-widest-3"
        style={{ fontFamily: FONTS.bodyBold, color: AMBER, letterSpacing: 2 }}
      >
        SECURITY CHECK
      </Text>
      <Text className="text-[28px] leading-tight text-text" style={{ fontFamily: FONTS.display }}>
        Two-factor verification.
      </Text>
      <Text className="mt-2 text-[14px] leading-6" style={{ fontFamily: FONTS.bodyLight, color: SUBTITLE }}>
        Enter the 6-digit code from your authenticator app.
      </Text>

      <View className="mt-6">
        <Input
          ref={inputRef}
          label="Code"
          value={code}
          onChangeText={onChangeCode}
          placeholder="000000"
          keyboardType="number-pad"
          maxLength={6}
          autoFocus
          autoComplete="one-time-code"
          textContentType="oneTimeCode"
          returnKeyType="go"
          onSubmitEditing={() => void onVerify()}
        />
      </View>

      {err ? (
        <Text className="mt-3 text-center text-sm text-red-600" style={{ fontFamily: FONTS.body }}>
          {err}
        </Text>
      ) : null}

      <View className="mt-4">
        <Button variant="primary" loading={busy} onPress={() => void onVerify()}>
          VERIFY →
        </Button>
      </View>

      <Pressable
        onPress={() => void Linking.openURL(SUPPORT_MAIL)}
        className="mt-5 self-center"
        style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
      >
        <Text className="text-center text-[12px]" style={{ fontFamily: FONTS.bodyMedium, color: AMBER }}>
          Lost your device? Contact support
        </Text>
      </Pressable>
    </AuthCard>
  );
}
